import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, AlignLeft, ArrowLeft, Calendar, CheckCircle2, ExternalLink, Pause, Play, RefreshCw, Repeat, ShieldCheck, Trash2 } from 'lucide-react'
import { useProvideFeedbackContext } from '../components/feedback/FeedbackContext'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { ActionMenu } from '../components/ui/ActionMenu'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { Timestamp } from '../components/ui/Timestamp'
import { LifeAreasPicker } from '../components/ui/LifeAreasPicker'
import { useToast } from '../components/ui/Toast'
import { useUserTimezone } from '../hooks/useUserTimezone'
import { useSkillCheckpoints } from '../hooks/useSkillCheckpoints'
import { SkillCheckpointCard } from '../components/automom/SkillCheckpointCard'
import { SkillService, type Skill, type SkillBoardCard } from '../services/skillService'
import { SkillGapsCard } from '../components/skills/SkillGapsCard'
import { SkillKeepingUp } from '../components/skills/SkillKeepingUp'
import { SkillPath } from '../components/skills/SkillPath'
import { SkillHoldMeToIt } from '../components/skills/SkillHoldMeToIt'
import {
  SkillAddRow, SkillCadenceEditor, SkillLevelsEditor, SkillParentEditor, SkillPriorityEditor, SkillTargetDateEditor, SkillTitleEditor, SkillWhyEditor,
} from '../components/skills/inline/SkillInlineEditors'
import { SKILL_PRIORITY_OPTIONS } from '../utils/skillOptions'
import { cadenceWords, levelWords, nextWhenWords } from '../utils/skillWords'
import { SECTION_TITLE, sectionOf, skillState, targetPassed } from '../utils/skillSections'
import { describeTaskCleanup } from '../utils/taskForm'
import DreamGraphTabs from '../components/hierarchy/DreamGraphTabs'
import BlockersSection from '../components/blockers/BlockersSection'
import { OccurrencesSection } from '../components/occurrences/OccurrencesSection'
import type { JourneyPlan } from '../types/journey'

/**
 * The skill page (Skills UX pass 2026-09-06), in the task page's frame:
 * breadcrumb → title with one summary line → the verbs → body | rail. Every
 * fact is edited where it is shown; the Add row offers what is not set yet;
 * pause, resume and "good enough for now" are verbs, not a select. The rail
 * carries the canonical order — Fill the gaps · Plan · Hold me to it · Keeping
 * up.
 *
 * Owner 2026-09-06: a skill is made up of tasks and habits. Nothing is logged
 * or scheduled on the skill itself — Step by step shows the items being
 * tracked, Hold me to it displays what is set on the live items, the practice
 * log is what got done under it. A field is in the UI only when the person
 * can do something with it; the classification, confidence and evidence
 * fields stay in the backend.
 */
const SECTION_HEADING = 'flex items-center gap-1.5 text-app-meta font-bold uppercase tracking-widest text-secondary'
const pad = (n: number) => String(n).padStart(2, '0')
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }

export const SkillDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const { timezone } = useUserTimezone()
  const [skill, setSkill] = useState<Skill | null>(null)
  const [card, setCard] = useState<SkillBoardCard | null>(null)
  const [plan, setPlan] = useState<JourneyPlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [levelsOpen, setLevelsOpen] = useState(0)
  const [dateOpen, setDateOpen] = useState(0)
  const skillService = useMemo(() => new SkillService(), [])
  const skillNumericId = id ? parseInt(id, 10) : NaN
  const checkpoints = useSkillCheckpoints()
  const checkpoint = checkpoints.prompts.find((p) => Number(p.skill_id) === skillNumericId) || null
  const autoStart = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('journey') === 'start'

  useProvideFeedbackContext({ pageType: 'skill_detail', entityType: 'skill', entityId: id })

  const loadCard = useCallback(async () => {
    if (!id) return
    try {
      const board = await skillService.getBoard(todayKey())
      setCard(board.skills[String(id)] || null)
    } catch { setCard(null) }
  }, [skillService, id])

  const load = useCallback(async () => {
    if (!id) return
    try {
      setError(null)
      const data = await skillService.getSkill(id)
      setSkill(data)
      void loadCard()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load skill')
    } finally {
      setLoading(false)
    }
  }, [skillService, id, loadCard])

  useEffect(() => { void load() }, [load])

  const onChanged = (s: Skill) => { setSkill(s); void loadCard() }
  const onPlan = useCallback((p: JourneyPlan | null) => { setPlan(p); void loadCard() }, [loadCard])

  // ── verbs ──
  const stage = async (next: 'active' | 'paused' | 'sufficient_for_goal') => {
    if (!skill) return
    setBusy(next)
    try {
      await skillService.updateSkill(String(skill.id), { skill_status: next })
      onChanged(await skillService.getSkill(String(skill.id)))
      if (next === 'paused') toast.toast({ variant: 'info', title: 'Paused', description: 'It waits here until you pick it back up. Nothing will nag you meanwhile.' })
      if (next === 'sufficient_for_goal') toast.toast({ variant: 'info', title: 'Good enough for now', description: 'That is a real place to stop. It stays on your list, resting.' })
      if (next === 'active') toast.success('Back on', 'Pick up where you left off.')
    } catch (err) {
      toast.error('That did not go through', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setBusy(null)
    }
  }

  const reachedTarget = async () => {
    if (!skill?.target_level) { setLevelsOpen((n) => n + 1); return }
    await skillService.updateSkill(String(skill.id), { current_level: skill.target_level, target_date: null })
    onChanged(await skillService.getSkill(String(skill.id)))
    toast.success('There you are', `${levelWords({ current_level: skill.target_level, target_level: null }) || 'Level'} it is.`)
  }

  const confirmDelete = async () => {
    if (!skill) return
    try {
      const result = await skillService.deleteSkill(String(skill.id))
      setDeleteOpen(false)
      toast.toast({ variant: 'success', title: 'Skill deleted', description: describeTaskCleanup(result.cleanup) || undefined })
      navigate('/skills')
    } catch (err) {
      setDeleteOpen(false)
      toast.error('Could not delete it', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  // ── frame ──
  if (loading) {
    return <div className="flex h-64 items-center justify-center"><RefreshCw className="h-6 w-6 animate-spin text-brand" /><span className="ml-3 text-secondary">Loading skill…</span></div>
  }
  if (error || !skill) {
    return (
      <div className="space-y-4">
        <Link to="/skills" className="inline-flex items-center gap-1 text-app-meta text-muted hover:text-brand"><ArrowLeft className="h-3.5 w-3.5" /> Skills</Link>
        <Card className="border-bad/30"><CardContent className="pt-6"><div className="py-8 text-center" role="alert">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-bad" />
          <h2 className="mb-2 text-xl font-semibold text-primary">{error || 'This skill is not here'}</h2>
          <Button onClick={() => navigate('/skills')}>Go to skills</Button>
        </div></CardContent></Card>
      </div>
    )
  }

  const state = skillState(skill)
  const resting = state.key === 'paused'          // parked is rationing's word; the skill stays live
  const enough = state.key === 'enough'
  const live = !resting && !enough
  const practisedToday = Boolean(card?.today)
  const stateDot = enough ? 'bg-ok' : resting ? 'bg-muted' : practisedToday ? 'bg-ok' : 'bg-brand'
  const priorityWord = SKILL_PRIORITY_OPTIONS.find((o) => o.value === skill.priority)?.label
  const passed = live && targetPassed(skill, todayKey())
  const levels = levelWords(skill)
  const nextWhen = nextWhenWords(card?.next, timezone)
  // The state word is the list's section word (North Star rule 2): the page
  // said "Building" over a row filed under "Ready to check".
  const stateWords = state.words || (practisedToday ? 'Practised today' : SECTION_TITLE[sectionOf(skill, card, todayKey())])
  // One filled button (rule 3): the next thing to practise; Resume while
  // paused; Pick it back up once good enough; the review banner's "I am
  // there" when the date has passed; else "Plan the journey" in the body.
  const nextLeads = live && Boolean(card?.next) && !passed && !checkpoint
  const planLeads = live && !card?.next && !passed && !plan && !checkpoint

  type Fact = { key: string; label: string; value: React.ReactNode }
  const facts: Fact[] = []
  if (skill.parent?.title) facts.push({ key: 'parent', label: 'Belongs to', value: <span className="inline-flex items-center gap-1"><Link to={`/goals/${skill.parent.id}`} className="hover:text-brand hover:underline">{skill.parent.title}</Link>{live && <SkillParentEditor skill={skill} onChanged={onChanged} variant="icon" trigger={<span aria-label="Change" className="text-app-micro">✎</span>} />}</span> })
  if (skill.priority === 'high') facts.push({ key: 'priority', label: 'Priority', value: live ? <SkillPriorityEditor skill={skill} onChanged={onChanged} /> : priorityWord })

  const menuItems = [
    ...(live ? [{ label: 'Good enough for now', icon: <ShieldCheck className="h-4 w-4" />, onSelect: () => void stage('sufficient_for_goal'), testId: 'skill-enough' }] : []),
    ...(enough ? [{ label: 'Pick it back up', icon: <Play className="h-4 w-4" />, onSelect: () => void stage('active'), testId: 'skill-pick-up' }] : []),
    { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onSelect: () => setDeleteOpen(true), danger: true, testId: 'skill-delete' },
  ]

  return (
    <div className="space-y-4">
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-app-meta text-muted" data-testid="skill-breadcrumb">
        <Link to="/skills" className="inline-flex shrink-0 items-center gap-1 hover:text-brand"><ArrowLeft className="h-3.5 w-3.5" /> Skills</Link>
        {skill.parent?.title && (<><span aria-hidden="true">/</span><Link to={`/goals/${skill.parent.id}`} className="truncate hover:text-brand" data-testid="skill-parent-link">{skill.parent.title}</Link></>)}
      </nav>

      {checkpoint && (
        <SkillCheckpointCard prompt={checkpoint} markShown={checkpoints.markShown} respond={checkpoints.respond} onDone={() => { void checkpoints.refetch(); void load() }} />
      )}

      {passed && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand/30 bg-brand-subtle p-3" role="status" data-testid="skill-target-review">
          <span className="text-app-meta text-primary">The date you set for this has passed. How is it going?</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button size="xs" onClick={() => void reachedTarget()} data-testid="skill-review-there"><CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />I am there</Button>
            <Button size="xs" variant="outline" onClick={() => setDateOpen((n) => n + 1)} data-testid="skill-review-new-date">Still building · pick a new date</Button>
            <Button size="xs" variant="outline" onClick={() => void stage('sufficient_for_goal')} data-testid="skill-review-enough">Good enough for now</Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-4">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <CardTitle className={`text-xl leading-tight sm:text-2xl ${enough ? 'text-secondary' : 'text-primary'}`}>
              {live ? <SkillTitleEditor skill={skill} onChanged={onChanged} trigger={skill.title} /> : skill.title}
            </CardTitle>
            <div className="inline-flex flex-wrap items-center gap-x-1.5 text-app-meta text-secondary" data-testid="skill-summary-line">
              <span className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${stateDot}`} aria-hidden="true" />{stateWords}</span>
              <span>· {live ? <SkillLevelsEditor skill={skill} onChanged={onChanged} openRequest={levelsOpen} trigger={levels || 'set where you are'} /> : (levels || 'no level set')}</span>
              <span>· <LifeAreasPicker value={(skill.life_areas && skill.life_areas.length ? skill.life_areas : skill.life_area ? [skill.life_area] : [])} editable={live}
                                       onSave={async (values) => { await skillService.updateSkill(String(skill.id), { life_areas: values }); onChanged(await skillService.getSkill(String(skill.id))) }} testId="skill-life-area" /></span>
              {practisedToday && card?.today && <span className="text-ok">· {card.today.kind === 'habit' ? 'habit done' : 'step done'} {card.today.at ? <Timestamp value={card.today.at} mode="time" /> : 'today'}</span>}
              {!practisedToday && card?.last_at && <span>· last practised <Timestamp value={card.last_at} mode="datetime" /></span>}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="skill-actions">
            {live && card?.next && (
              <Button variant={nextLeads ? 'default' : 'outline'} size="xs" className={nextLeads ? '' : 'hover:bg-brand-subtle'} onClick={() => navigate(`/${card.next!.kind}s/${card.next!.id}`)} data-testid="skill-next-step">
                {card.next.kind === 'habit' ? <Repeat className="mr-2 h-4 w-4" /> : <ExternalLink className="mr-2 h-4 w-4" />}{card.next.kind === 'habit' ? 'Next: the habit' : 'Next step'}
              </Button>
            )}
            {live && (
              <Button variant="outline" size="xs" className="hover:bg-brand-subtle" disabled={busy === 'paused'} onClick={() => void stage('paused')} data-testid="skill-pause">
                <Pause className="mr-2 h-4 w-4" />Pause
              </Button>
            )}
            {state.key === 'paused' && (
              <Button size="xs" disabled={busy === 'active'} onClick={() => void stage('active')} data-testid="skill-resume">
                <Play className="mr-2 h-4 w-4" />Resume
              </Button>
            )}
            {enough && (
              <Button size="xs" disabled={busy === 'active'} onClick={() => void stage('active')} data-testid="skill-pick-up-button">
                <Play className="mr-2 h-4 w-4" />Pick it back up
              </Button>
            )}
            <ActionMenu label={`More for ${skill.title}`} items={menuItems} testId="skill-more" />
          </div>
        </CardHeader>

        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <div className="grid gap-6 border-t border-soft pt-4 lg:grid-cols-5">
            <div className="min-w-0 space-y-5 lg:col-span-3" data-testid="skill-body">
              <section data-testid="skill-about">
                <h3 className={SECTION_HEADING}><AlignLeft className="h-3.5 w-3.5 text-brand" /> About</h3>
                <p className="mt-2 text-sm text-primary" data-testid="skill-why">
                  {live ? <SkillWhyEditor skill={skill} onChanged={onChanged} trigger={skill.description || undefined} /> : (skill.description || <span className="text-muted">Nothing written yet.</span>)}
                </p>
                {facts.length > 0 && (
                  <dl className="mt-3 text-sm" data-testid="skill-facts">
                    {facts.map((f) => (
                      <div key={f.key} className="flex gap-3 py-1" data-testid={`skill-fact-${f.key}`}>
                        <dt className="w-28 shrink-0 pt-0.5 text-app-meta text-muted">{f.label}</dt>
                        <dd className="min-w-0 break-words text-primary">{f.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {live && <div className="mt-3"><SkillAddRow skill={skill} onChanged={onChanged} /></div>}
              </section>

              <SkillPath skill={skill} card={card} live={live} headingClass={SECTION_HEADING} tz={timezone} onPlan={onPlan} refreshKey={card?.done_tasks ?? null} autoStart={autoStart} leads={planLeads} />

              {/* Just the log: Keeping up owns the count, the strip and the last time. */}
              <OccurrencesSection itemType="skill" itemId={Number(skill.id)} refreshKey={`${skill.updated_at || ''}:${card?.today?.id || ''}:${card?.days_total ?? ''}`} headingClass={SECTION_HEADING} logOnly />

              {!Number.isNaN(skillNumericId) && <BlockersSection itemType="skill" itemId={skillNumericId} className="rounded-xl border border-soft bg-surface-alt p-4" />}
            </div>

            <aside className="min-w-0 space-y-4 rounded-xl bg-surface-alt p-4 lg:col-span-2" data-testid="skill-rail">
              {live && <SkillGapsCard skill={skill} onChanged={onChanged} embedded />}

              <div className={live ? 'border-t border-soft pt-4' : ''} data-testid="skill-plan">
                <h3 className={SECTION_HEADING}><Calendar className="h-3.5 w-3.5 text-brand" /> Plan</h3>
                <div className="mt-1.5 space-y-1 text-sm text-primary">
                  <div data-testid="skill-plan-next">
                    {card?.next ? (
                      <span>Next: <Link to={`/${card.next.kind}s/${card.next.id}`} className="font-medium hover:text-brand hover:underline">{card.next.title}</Link>{nextWhen ? <span className="text-secondary"> · {nextWhen}</span> : <span className="text-secondary"> · not on the calendar yet</span>}</span>
                    ) : plan && plan.status === 'completed' ? (
                      <span className="text-secondary">The journey is complete. Plan a new one, or call it good enough.</span>
                    ) : (
                      <span className="text-secondary">{live ? 'No next step yet · plan the journey below' : 'Nothing planned'}</span>
                    )}
                  </div>
                  {/* Plan holds what is set (2026-09-12). An unset by-when or cadence is
                      offered once, in the Add row and the gaps card — the block used to
                      ask "By when?" and "How often?" as a third and fourth prompt. The
                      date editor stays mounted while a review banner may open it. */}
                  {(skill.target_date || (live && passed)) && (
                    <div data-testid="skill-plan-by">
                      {live
                        ? <SkillTargetDateEditor skill={skill} onChanged={onChanged} openRequest={dateOpen} trigger={skill.target_date ? <>Get there by <Timestamp value={`${skill.target_date.slice(0, 10)}T12:00:00`} mode="date" /></> : 'Pick a new date'} />
                        : (skill.target_date ? <>Get there by <Timestamp value={`${skill.target_date.slice(0, 10)}T12:00:00`} mode="date" /></> : null)}
                    </div>
                  )}
                  {skill.practice_cadence && (
                    <div>
                      {live ? <SkillCadenceEditor skill={skill} onChanged={onChanged} trigger={`Practise ${(cadenceWords(skill.practice_cadence) || '').toLowerCase()}`} /> : `Practise ${(cadenceWords(skill.practice_cadence) || '').toLowerCase()}`}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-soft pt-4">
                <SkillHoldMeToIt card={card} headingClass={SECTION_HEADING} />
              </div>

              <div className="border-t border-soft pt-4">
                <SkillKeepingUp skill={skill} card={card} headingClass={SECTION_HEADING} />
              </div>
              <p className="text-app-micro text-muted">Added <Timestamp value={skill.created_at} mode="date" />{skill.updated_at ? <> · changed <Timestamp value={skill.updated_at} mode="datetime" /></> : null}</p>
            </aside>
          </div>
        </CardContent>
      </Card>

      {skill?.id && <DreamGraphTabs nodeId={Number(skill.id)} nodeType="skill" />}

      <ConfirmationModal isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={confirmDelete} title="Delete this skill?"
                         message={`“${skill.title}” will be removed, along with the path steps not yet started. Tasks and habits you made under it stay, on their own.`} variant="error" confirmText="Delete" />
    </div>
  )

}

export default SkillDetail
